import { youtubeSearchApi } from '@edulastic/api'

/**
 * Helpers to make Froala-embedded YouTube `<iframe>`s satisfy YouTube's v2
 * Embedder Identity enforcement (raw, tokenless embeds fail with error 152).
 *
 * The strategy keeps the original `<iframe>` element intact (so the edit and
 * preview views look identical) and only rewrites its `src` to carry the
 * encrypted `embed_config` token — the same URL the YT IFrame API produces.
 * The token is IP/time-bound and MUST NOT be persisted, so it is always
 * stripped again before content is saved.
 */

const YT_EMBED_TEST = /youtube(-nocookie)?\.com\/embed\//i
const YT_EMBED_ID = /youtube(-nocookie)?\.com\/embed\/([\w-]{11})/i

/** Cheap guard so callers can skip all work on non-video content. */
export const hasYoutubeEmbed = (html = '') => YT_EMBED_TEST.test(html)

const extractVideoId = (src = '') => {
  const match = src.match(YT_EMBED_ID)
  return match ? match[2] : null
}

/** Drop our ephemeral `embed_config` param, leaving the clean, storable src. */
const stripEmbedConfig = (src = '') => {
  const [base, query = ''] = src.split('?')
  if (!query) return src
  const params = new URLSearchParams(query)
  params.delete('embed_config')
  const rest = params.toString()
  return rest ? `${base}?${rest}` : base
}

/** Build the tokenized embed src (clean base + enablejsapi + origin + token). */
const buildTokenizedSrc = (src, enc) => {
  const [base, query = ''] = stripEmbedConfig(src).split('?')
  const params = new URLSearchParams(query)
  params.set('enablejsapi', '1')
  if (typeof window !== 'undefined' && window.location) {
    params.set('origin', window.location.origin)
  }
  params.set('embed_config', JSON.stringify({ enc }))
  return `${base}?${params.toString()}`
}

/**
 * Preview only: stash each YouTube iframe's src into `data-yt-src` and blank the
 * live `src`, so the raw (tokenless) embed never loads before the token is
 * applied. Pure, synchronous string transform.
 */
export const stashYoutubeIframes = (html = '') => {
  if (!hasYoutubeEmbed(html) || typeof DOMParser === 'undefined') return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  let changed = false
  doc.querySelectorAll('iframe').forEach((frame) => {
    const src = frame.getAttribute('src') || ''
    if (!YT_EMBED_TEST.test(src)) return
    frame.setAttribute('data-yt-src', stripEmbedConfig(src))
    frame.setAttribute('src', 'about:blank')
    changed = true
  })
  return changed ? doc.body.innerHTML : html
}

/**
 * Fetch tokens and apply the tokenized src to every not-yet-tokenized YouTube
 * iframe within `root`. Reads the stashed `data-yt-src` when present (preview),
 * otherwise the live `src` (editor). On failure it restores the original src so
 * a thumbnail still shows rather than a blank frame.
 */
export const tokenizeYoutubeIframes = async (root) => {
  if (!root || typeof root.querySelectorAll !== 'function') return
  const frames = Array.from(root.querySelectorAll('iframe')).filter((frame) => {
    if (frame.getAttribute('data-yt-tokenized')) return false
    const raw = frame.getAttribute('data-yt-src') || frame.getAttribute('src')
    return YT_EMBED_TEST.test(raw || '')
  })

  await Promise.all(
    frames.map(async (frame) => {
      const raw = frame.getAttribute('data-yt-src') || frame.getAttribute('src')
      const videoId = extractVideoId(raw || '')
      if (!videoId) return
      try {
        const { enc } = await youtubeSearchApi.fetchEmbedToken({ videoId })
        if (!enc) throw new Error('empty enc')
        frame.setAttribute('data-yt-src', stripEmbedConfig(raw))
        frame.setAttribute('data-yt-tokenized', '1')
        frame.setAttribute('src', buildTokenizedSrc(raw, enc))
      } catch (error) {
        // Restore the original src (if it was stashed) so a thumbnail shows.
        const stashed = frame.getAttribute('data-yt-src')
        if (stashed && frame.getAttribute('src') === 'about:blank') {
          frame.setAttribute('src', stashed)
        }
      }
    })
  )
}

/**
 * Save path: restore the clean YouTube iframe src (no token, no marker attrs)
 * so the ephemeral token is never written to stored content.
 */
export const cleanYoutubeIframesForSave = (html = '') => {
  if (typeof DOMParser === 'undefined') return html
  if (!hasYoutubeEmbed(html) && !/data-yt-src/.test(html)) return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  let changed = false
  doc.querySelectorAll('iframe').forEach((frame) => {
    const src = frame.getAttribute('src') || ''
    const stashed = frame.getAttribute('data-yt-src')
    if (!YT_EMBED_TEST.test(src) && !stashed) return
    frame.setAttribute('src', stripEmbedConfig(stashed || src))
    frame.removeAttribute('data-yt-src')
    frame.removeAttribute('data-yt-tokenized')
    changed = true
  })
  return changed ? doc.body.innerHTML : html
}
